import React, { useState, useMemo } from 'react';
import {
  Table, Card, Input, Button, Space, Tag, Typography, Form, Select, Modal,
  Drawer, Descriptions, Alert, Popconfirm, message, Segmented,
} from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// ──────────────────────────────────────────────
// Mock 数据 — TODO: 联调时替换为接口数据
// 改号工单：由方案商创建，用于把已烧号模组（设备）的 License 所属产品改为目标整机
// ──────────────────────────────────────────────

const STATUS_MAP = {
  processing: { text: '进行中', color: 'processing' },
  closed: { text: '已关闭', color: 'default' },
};

// 产测分组（控制哪些产测账号可执行该工单）
const PRODUCE_GROUPS = [
  { value: 'default', label: '默认分组' },
  { value: 'east', label: '华东生产部' },
  { value: 'south', label: '华南外协厂' },
];

const GROUP_LABEL = PRODUCE_GROUPS.reduce((map, g) => ({ ...map, [g.value]: g.label }), {});

// 当前登录企业（原型用）
const CURRENT_ENTERPRISE = { id: 'ENT_M01', name: '深圳模组科技有限公司' };

// 方案产品
const ORIGIN_PRODUCTS = [
  { value: 'P_ECO_001', label: '智能云台摄像机方案（P_ECO_001）' },
  { value: 'P_ECO_002', label: '4G低功耗电池摄像机方案（P_ECO_002）' },
  { value: 'P_ECO_003', label: '智能门铃方案（P_ECO_003）' },
];

// 目标整机：按方案产品分组，选择后带出整机企业
const DERIVED_PRODUCTS = {
  P_ECO_001: [
    { value: 'P_DERIVED_001', label: '智能云台摄像机-康林高配版（P_DERIVED_001）', companyName: '东莞康林物联网有限公司', companyId: 'ENT_C01' },
    { value: 'P_DERIVED_002', label: '智能云台摄像机-云视标准版（P_DERIVED_002）', companyName: '成都云视科技有限公司', companyId: 'ENT_C02' },
    { value: 'P_DERIVED_003', label: '智能云台摄像机-康林低配版（P_DERIVED_003）', companyName: '东莞康林物联网有限公司', companyId: 'ENT_C01' },
  ],
  P_ECO_002: [
    { value: 'P_DERIVED_004', label: '4G电池摄像机-康林版（P_DERIVED_004）', companyName: '东莞康林物联网有限公司', companyId: 'ENT_C01' },
  ],
  P_ECO_003: [
    { value: 'P_DERIVED_005', label: '智能门铃-云视定制版（P_DERIVED_005）', companyName: '成都云视科技有限公司', companyId: 'ENT_C02' },
  ],
};

const MOCK_ORDERS = [
  {
    key: 'WO-CHG-001', orderName: '云台方案→康林高配版 改号',
    fromProductName: '智能云台摄像机方案', fromProductId: 'P_ECO_001',
    toProductName: '智能云台摄像机-康林高配版', toProductId: 'P_DERIVED_001',
    toCompanyName: '东莞康林物联网有限公司', toCompanyId: 'ENT_C01',
    creatorCompanyName: '深圳模组科技有限公司', creatorCompanyId: 'ENT_M01',
    completedCount: 120, status: 'processing', produceGroup: 'default', createdAt: '2026-04-21 09:00:00', remark: '4 月批次模组改号',
  },
  {
    key: 'WO-CHG-002', orderName: '云台方案→云视标准版 改号',
    fromProductName: '智能云台摄像机方案', fromProductId: 'P_ECO_001',
    toProductName: '智能云台摄像机-云视标准版', toProductId: 'P_DERIVED_002',
    toCompanyName: '成都云视科技有限公司', toCompanyId: 'ENT_C02',
    creatorCompanyName: '深圳模组科技有限公司', creatorCompanyId: 'ENT_M01',
    completedCount: 80, status: 'processing', produceGroup: 'east', createdAt: '2026-04-22 14:00:00', remark: null,
  },
  {
    key: 'WO-CHG-003', orderName: '云台方案→康林低配版 改号',
    fromProductName: '智能云台摄像机方案', fromProductId: 'P_ECO_001',
    toProductName: '智能云台摄像机-康林低配版', toProductId: 'P_DERIVED_003',
    toCompanyName: '东莞康林物联网有限公司', toCompanyId: 'ENT_C01',
    creatorCompanyName: '深圳模组科技有限公司', creatorCompanyId: 'ENT_M01',
    completedCount: 200, status: 'closed', produceGroup: 'south', createdAt: '2026-03-10 10:00:00', remark: '已批量完成，关闭',
  },
  {
    key: 'WO-CHG-004', orderName: '4G 电池机→康林版 改号',
    fromProductName: '4G低功耗电池摄像机方案', fromProductId: 'P_ECO_002',
    toProductName: '4G电池摄像机-康林版', toProductId: 'P_DERIVED_004',
    toCompanyName: '东莞康林物联网有限公司', toCompanyId: 'ENT_C01',
    creatorCompanyName: '杭州云联物联网有限公司', creatorCompanyId: 'ENT_M02',
    completedCount: 0, status: 'processing', produceGroup: 'default', createdAt: '2026-04-25 16:30:00', remark: null,
  },
];

// 工单下的 License 明细
const MOCK_ORDER_LICENSES = {
  'WO-CHG-001': [
    { key: 'l1', license: 'A01M012604210133', result: '完成', changedAt: '2026-04-21 10:12:33', account: 'prod_line_a' },
    { key: 'l2', license: 'A01M012604210267', result: '完成', changedAt: '2026-04-21 10:13:01', account: 'prod_line_a' },
    { key: 'l3', license: 'A01M012604210310', result: '失败', changedAt: '2026-04-21 10:15:44', account: 'prod_line_a' },
    { key: 'l4', license: 'A01M012604210422', result: '完成', changedAt: '2026-04-21 10:18:20', account: 'prod_line_b' },
  ],
  'WO-CHG-002': [
    { key: 'l5', license: 'A01M012604220155', result: '完成', changedAt: '2026-04-22 14:20:10', account: 'prod_line_a' },
    { key: 'l6', license: 'A01M012604220188', result: '完成', changedAt: '2026-04-22 14:22:37', account: 'prod_line_a' },
  ],
  'WO-CHG-003': [
    { key: 'l7', license: 'A01M012603100011', result: '完成', changedAt: '2026-03-10 11:00:00', account: 'prod_line_c' },
    { key: 'l8', license: 'A01M012603100022', result: '完成', changedAt: '2026-03-10 11:01:12', account: 'prod_line_c' },
  ],
  'WO-CHG-004': [],
};

const ProductCell = ({ name, id }) => (
  <div>
    <div style={{ fontWeight: 500 }}>{name}</div>
    <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{id}</Text>
  </div>
);

const CompanyCell = ({ name, id }) => (
  <div>
    <div style={{ fontWeight: 500 }}>{name}</div>
    <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{id}</Text>
  </div>
);

const ChangeNumberOrder = () => {
  const [orders, setOrders] = useState(MOCK_ORDERS);
  const [view, setView] = useState('enterprise');

  // 查询条件
  const [queryType, setQueryType] = useState('name');
  const [keyword, setKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState(undefined);
  const [applied, setApplied] = useState({ type: 'name', keyword: '', status: undefined });

  // 创建工单
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [originProduct, setOriginProduct] = useState(undefined);
  const [targetProduct, setTargetProduct] = useState(undefined);

  // 明细抽屉
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOrder, setDetailOrder] = useState(null);

  // 批量修改分组
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkGroup, setBulkGroup] = useState(undefined);

  const derivedOptions = useMemo(
    () => (originProduct ? (DERIVED_PRODUCTS[originProduct] || []) : []),
    [originProduct],
  );

  const selectedDerived = useMemo(
    () => derivedOptions.find((p) => p.value === targetProduct),
    [derivedOptions, targetProduct],
  );

  const filtered = useMemo(() => {
    let list = orders;
    if (view === 'enterprise') {
      list = list.filter((r) => r.creatorCompanyId === CURRENT_ENTERPRISE.id);
    }
    if (applied.keyword.trim()) {
      const kw = applied.keyword.trim().toLowerCase();
      list = list.filter((r) => (applied.type === 'id'
        ? r.key.toLowerCase().includes(kw)
        : r.orderName.toLowerCase().includes(kw)));
    }
    if (applied.status) {
      list = list.filter((r) => r.status === applied.status);
    }
    return list;
  }, [orders, applied, view]);

  const handleSearch = () => setApplied({ type: queryType, keyword, status: statusFilter });

  const handleReset = () => {
    setQueryType('name');
    setKeyword('');
    setStatusFilter(undefined);
    setApplied({ type: 'name', keyword: '', status: undefined });
  };

  const handleViewChange = (val) => {
    setView(val);
    setSelectedRowKeys([]);
    handleReset();
  };

  const openCreate = () => {
    createForm.resetFields();
    createForm.setFieldsValue({ produceGroup: 'default' });
    setOriginProduct(undefined);
    setTargetProduct(undefined);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    try {
      await createForm.validateFields();
    } catch {
      return;
    }
    if (!selectedDerived) {
      message.warning('请选择目标整机');
      return;
    }
    const origin = ORIGIN_PRODUCTS.find((p) => p.value === originProduct);
    const newOrder = {
      key: `WO-CHG-${String(orders.length + 1).padStart(3, '0')}`,
      orderName: createForm.getFieldValue('orderName'),
      fromProductName: origin.label.split('（')[0],
      fromProductId: originProduct,
      toProductName: selectedDerived.label.split('（')[0],
      toProductId: selectedDerived.value,
      toCompanyName: selectedDerived.companyName,
      toCompanyId: selectedDerived.companyId,
      completedCount: 0,
      status: 'processing',
      produceGroup: createForm.getFieldValue('produceGroup'),
      createdAt: new Date().toLocaleString('zh-CN'),
      remark: createForm.getFieldValue('remark') || null,
    };
    setOrders((prev) => [newOrder, ...prev]);
    setCreateOpen(false);
    message.success('改号工单已创建，将同步至产测工具');
  };

  const handleClose = (record) => {
    setOrders((prev) => prev.map((o) => (o.key === record.key ? { ...o, status: 'closed' } : o)));
    message.success('工单已关闭，不会再同步至产测工具');
  };

  const handleBulkApply = () => {
    if (!bulkGroup) { message.warning('请选择产测分组'); return; }
    setOrders((prev) => prev.map((o) => (
      selectedRowKeys.includes(o.key) ? { ...o, produceGroup: bulkGroup } : o
    )));
    message.success(`已修改 ${selectedRowKeys.length} 个工单的产测分组`);
    setBulkOpen(false);
    setBulkGroup(undefined);
    setSelectedRowKeys([]);
  };

  const openDetail = (record) => {
    setDetailOrder(record);
    setDetailOpen(true);
  };

  const columns = [
    {
      title: '工单名称', key: 'orderName', width: 240,
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.orderName}</div>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>{r.key}</Text>
        </div>
      ),
    },
    { title: '方案产品', key: 'fromProduct', width: 220, render: (_, r) => <ProductCell name={r.fromProductName} id={r.fromProductId} /> },
    { title: '目标整机', key: 'toProduct', width: 240, render: (_, r) => <ProductCell name={r.toProductName} id={r.toProductId} /> },
    { title: '整机企业', key: 'toCompany', width: 240, render: (_, r) => <CompanyCell name={r.toCompanyName} id={r.toCompanyId} /> },
    ...(view === 'platform'
      ? [{ title: '创建企业', key: 'creatorCompany', width: 220, render: (_, r) => <CompanyCell name={r.creatorCompanyName} id={r.creatorCompanyId} /> }]
      : []),
    { title: '已完成', dataIndex: 'completedCount', width: 90, align: 'right', render: (v) => <Text strong>{v.toLocaleString()}</Text> },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (v) => <Tag color={STATUS_MAP[v]?.color} style={{ border: 'none' }}>{STATUS_MAP[v]?.text || v}</Tag>,
    },
    { title: '产测分组', dataIndex: 'produceGroup', width: 130, render: (v) => GROUP_LABEL[v] || v || '-' },
    { title: '创建时间', dataIndex: 'createdAt', width: 170 },
    {
      title: '操作', key: 'action', width: 180, fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => openDetail(r)}>查看明细</Button>
          {view !== 'platform' && r.status === 'processing' && (
            <Popconfirm
              title="确认关闭该工单？"
              description="关闭后不会再同步至产测工具。"
              onConfirm={() => handleClose(r)}
            >
              <Button type="link" size="small" danger>关闭工单</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const licenseColumns = [
    { title: 'License', dataIndex: 'license', width: 200, render: (t) => <Text copyable style={{ fontFamily: 'monospace' }}>{t}</Text> },
    {
      title: '变更结果', dataIndex: 'result', width: 100,
      render: (v) => <Tag color={v === '完成' ? 'success' : 'error'} style={{ border: 'none' }}>{v}</Tag>,
    },
    { title: '变更时间', dataIndex: 'changedAt', width: 170 },
    { title: '产测账号', dataIndex: 'account', width: 140 },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>改号工单</Title>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>mock 角色视角切换</Text>
          <Segmented
            value={view}
            onChange={handleViewChange}
            options={[{ label: '企业视角', value: 'enterprise' }, { label: '平台视角', value: 'platform' }]}
          />
        </Space>
      </div>

      <Alert
        type="info"
        showIcon
        closable
        style={{ marginBottom: 16 }}
        message="仅适用已烧号的模组（设备）改 License 的所属产品 ID 使用，由方案商创建此类工单。关闭后的工单不会再同步至产测工具。"
      />

      <Card bordered={false} style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form layout="inline" style={{ rowGap: 12 }}>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space.Compact>
              <Select
                value={queryType}
                onChange={(val) => {
                  setQueryType(val);
                  setKeyword('');
                }}
                style={{ width: 110 }}
                options={[{ value: 'name', label: '工单名称' }, { value: 'id', label: '工单ID' }]}
              />
              <Input
                placeholder={queryType === 'id' ? '请输入工单 ID' : '请输入工单名称'}
                allowClear
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 200 }}
              />
            </Space.Compact>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Select
              placeholder="工单状态"
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 140 }}
              options={Object.entries(STATUS_MAP).map(([value, v]) => ({ value, label: v.text }))}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card bordered={false} style={{ borderRadius: 12 }}>
        <div style={{ marginBottom: 16 }}>
          {view !== 'platform' && (
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>创建工单</Button>
              <Button disabled={selectedRowKeys.length === 0} onClick={() => setBulkOpen(true)}>
                批量修改分组{selectedRowKeys.length > 0 ? `（${selectedRowKeys.length}）` : ''}
              </Button>
            </Space>
          )}
        </div>
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="key"
          rowSelection={view !== 'platform' ? {
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          } : undefined}
          scroll={{ x: 1690 }}
          style={{ whiteSpace: 'nowrap' }}
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
          locale={{ emptyText: '暂无改号工单' }}
        />
      </Card>

      {/* 创建工单 */}
      <Modal
        title="创建改号工单"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        okText="创建"
        cancelText="取消"
        width={560}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item name="orderName" label="工单名称" rules={[{ required: true, message: '请输入工单名称' }]}>
            <Input placeholder="请输入工单名称" maxLength={50} showCount />
          </Form.Item>
          <Form.Item name="produceGroup" label="产测分组" rules={[{ required: true, message: '请选择产测分组' }]}>
            <Select placeholder="请选择产测分组" options={PRODUCE_GROUPS} />
          </Form.Item>
          <Form.Item name="fromProduct" label="方案产品" rules={[{ required: true, message: '请选择方案产品' }]}>
            <Select
              placeholder="请选择方案产品"
              options={ORIGIN_PRODUCTS}
              onChange={(val) => {
                setOriginProduct(val);
                setTargetProduct(undefined);
                createForm.setFieldsValue({ toProduct: undefined, toCompany: undefined });
              }}
            />
          </Form.Item>
          <Form.Item name="toProduct" label="目标整机" rules={[{ required: true, message: '请选择目标整机' }]}>
            <Select
              placeholder={originProduct ? '请选择目标整机' : '请先选择方案产品'}
              disabled={!originProduct}
              options={derivedOptions}
              onChange={(val) => {
                setTargetProduct(val);
                const target = derivedOptions.find((p) => p.value === val);
                createForm.setFieldValue('toCompany', target ? `${target.companyName}（${target.companyId}）` : undefined);
              }}
            />
          </Form.Item>
          <Form.Item name="toCompany" label="整机企业">
            <Input disabled placeholder="选择目标整机后自动带出" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="请输入备注" maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量修改分组 */}
      <Modal
        title="批量修改分组"
        open={bulkOpen}
        onCancel={() => setBulkOpen(false)}
        onOk={handleBulkApply}
        okText="确认"
        cancelText="取消"
        width={420}
        destroyOnClose
      >
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary">已选 {selectedRowKeys.length} 个工单</Text>
          </div>
          <Select
            placeholder="请选择产测分组"
            style={{ width: '100%' }}
            value={bulkGroup}
            onChange={setBulkGroup}
            options={PRODUCE_GROUPS}
          />
        </div>
      </Modal>

      {/* 工单明细 */}
      <Drawer
        title="改号工单明细"
        width={800}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        destroyOnClose
      >
        {detailOrder && (
          <>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label="工单名称" span={2}>{detailOrder.orderName}</Descriptions.Item>
              <Descriptions.Item label="工单号" span={2}>
                <Text style={{ fontFamily: 'monospace' }}>{detailOrder.key}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="创建企业">{detailOrder.creatorCompanyName}</Descriptions.Item>
              <Descriptions.Item label="创建企业ID">{detailOrder.creatorCompanyId}</Descriptions.Item>
              <Descriptions.Item label="方案产品">{detailOrder.fromProductName}</Descriptions.Item>
              <Descriptions.Item label="方案产品ID">{detailOrder.fromProductId}</Descriptions.Item>
              <Descriptions.Item label="目标整机">{detailOrder.toProductName}</Descriptions.Item>
              <Descriptions.Item label="目标整机ID">{detailOrder.toProductId}</Descriptions.Item>
              <Descriptions.Item label="整机企业">{detailOrder.toCompanyName}</Descriptions.Item>
              <Descriptions.Item label="整机企业ID">{detailOrder.toCompanyId}</Descriptions.Item>
              <Descriptions.Item label="已完成">{detailOrder.completedCount}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_MAP[detailOrder.status]?.color} style={{ border: 'none' }}>
                  {STATUS_MAP[detailOrder.status]?.text || detailOrder.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="产测分组">{GROUP_LABEL[detailOrder.produceGroup] || detailOrder.produceGroup || '-'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{detailOrder.createdAt}</Descriptions.Item>
              <Descriptions.Item label="备注">{detailOrder.remark || '-'}</Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12 }}>
              <Text strong>License 列表</Text>
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                （该工单下产测工具已上报的改号记录）
              </Text>
            </div>
            <Table
              size="small"
              columns={licenseColumns}
              dataSource={MOCK_ORDER_LICENSES[detailOrder.key] || []}
              pagination={false}
              scroll={{ x: 700 }}
              style={{ whiteSpace: 'nowrap' }}
              locale={{ emptyText: '暂无 License 记录' }}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ChangeNumberOrder;
